import { useGetSystemTabDataById } from "@features/ax/assetRegistry/systems/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { OverviewTab } from "./tabs/overviewTab";

export const FinanceLanding = () => {
  const currency = useCurrency();
  const { systemId } = useParams();
  const { data: financeData } = useGetSystemTabDataById(
    Number(systemId),
    "finance"
  );
  useEffect(() => {
    logEvent("finance_tab_viewed");
  }, []);
  const systemFinancialData = {
    currentBookValue: financeData?.systemBookValue?.currentBookValue,
    initialCapitalCost: financeData?.systemBookValue?.initialCapitalCost,
    accumulatedDepreciation:
      financeData?.systemBookValue?.accumulatedDepreciation,
    salvageValue: financeData?.systemBookValue?.salvageValue,
    annualDepreciation: financeData?.systemBookValue?.annualDepreciation,
    toolTipText: financeData?.systemBookValue?.toolTipText,
  };

  const urlMapping = {
    overview: "overview",
    history: "history",
  };

  const tabComponents = {
    overview: {
      label: "Overview",
      shortLabel: "Overview",
      component: <OverviewTab data={financeData} />,
    },
    // history: {
    //   label: "History",
    //   shortLabel: "History",
    //   component: <HistoryTab data={financeData} />,
    // },
    // {/* Removed for now will need after */}
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">System Book Value</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-3">
            <div>
              <div className="text-3xl font-bold text-blue-600">
                {currency} {systemFinancialData?.currentBookValue}
              </div>
              <div className="flex item-center gap-2">
                <div className="text-sm text-gray-600">Current Book Value</div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        The current value of the system on the balance sheet
                        after accounting for all depreciation to date.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {currency} {systemFinancialData?.initialCapitalCost}
              </div>
              <div className="flex item-center gap-2">
                <div className="text-sm text-gray-600">
                  Initial Capital Cost
                </div>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The total original cost to acquire and install this
                      system.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold text-red-600">
                {currency} {systemFinancialData?.accumulatedDepreciation}
              </div>
              <div className="flex item-center gap-2">
                <div className="text-sm text-gray-600">
                  Accumulated Depreciation
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        The sum of all depreciation expenses recorded for this
                        system over its life.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {currency} {systemFinancialData?.salvageValue}
              </div>
              <div className="flex item-center gap-2">
                <div className="text-sm text-gray-600">Salvage Value</div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        The estimated resale value of the system at the end of
                        its useful life
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Tabs
        defaultValue="overview"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        // tabsListClassName="mt-4 grid w-full gsrid-cols-2 mb-6"
        tabsListClassName="mt-4 mb-6"
        idPrefix="finance-tab"
        level={1}
      />
    </div>
  );
};
